import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useDashboard } from "../context/DashboardContext";
import {
  formatMainMeterOnlineCount,
  mainCConsensus,
  useFreqSparkline,
} from "../hooks/useFreqSparkline";
import type { HostDiskPayload, TopicId } from "../types/dashboard";
import { fleetPowerTotalW } from "../constants/chargerPower";
import { usePowerGamma } from "../context/PowerGammaContext";
import { formatTsMs } from "../utils/formatTsMs";
import { Server } from "lucide-react";
import { useEthBpsSparkline } from "../hooks/useEthBpsSparkline";
import { EthBpsSparkline, ETH_BPS_SPARKLINE_SERIES } from "./EthBpsSparkline";
import { FreqSparkline, METER_C_STYLES, METER_SPARKLINE_SERIES } from "./FreqSparkline";

type SidebarField = { label: string; value: string };

const DISK_HOST_LABELS = ["m4-1", "m4-2", "m1-1"] as const;
const DISK_STALE_MS = 120_000;

function diskUsedClass(pct: number | null, stale: boolean) {
  if (pct == null || stale) return "text-text-tertiary";
  if (pct >= 95) return "font-semibold text-status-error";
  if (pct >= 85) return "font-semibold text-status-warn";
  return "font-medium text-text-primary";
}

function formatDiskHost(
  hostDisk: HostDiskPayload | null | undefined,
  hostId: (typeof DISK_HOST_LABELS)[number],
  nowMs: number,
) {
  const sample = hostDisk?.hosts?.[hostId];
  const received = hostDisk?.received_at_ms?.[hostId];
  const stale =
    received == null || !Number.isFinite(received) || nowMs - received > DISK_STALE_MS;
  const pct =
    sample?.used_pct != null && Number.isFinite(Number(sample.used_pct))
      ? Number(sample.used_pct)
      : null;
  const label = pct != null ? `${pct.toFixed(2)}%` : "—";
  const title =
    pct != null && sample?.path
      ? `${hostId} ${sample.path}: ${pct.toFixed(2)}% used`
      : `${hostId}: waiting for infra/host/disk/${hostId}`;
  return { label, title, pct, stale };
}

function HostDiskReadout({ hostDisk }: { hostDisk: HostDiskPayload | null | undefined }) {
  const nowMs = Date.now();
  return (
    <div
      className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 pt-1 font-mono tabular-nums"
      style={{ fontSize: "var(--font-micro)" }}
    >
      <span className="font-medium text-text-primary">df</span>
      {DISK_HOST_LABELS.map((hostId) => {
        const { label, title, pct, stale } = formatDiskHost(hostDisk, hostId, nowMs);
        return (
          <span key={hostId} title={title} className="inline-flex items-baseline gap-1">
            <span className="text-text-tertiary">{hostId}</span>
            <span className={diskUsedClass(pct, stale)}>{label}</span>
          </span>
        );
      })}
    </div>
  );
}

function formatFreq(hz: number | undefined) {
  if (!Number.isFinite(hz)) return "—";
  return `${hz!.toFixed(3)} Hz`;
}

function formatMbps(v: number | null | undefined) {
  if (v == null || !Number.isFinite(v)) return "—";
  return `${v.toFixed(1)} Mbit/s`;
}

function formatPowerMw(w: number | null | undefined, scale: number) {
  if (w == null || !Number.isFinite(w)) return "—";
  return ((w * scale) / 1_000_000).toFixed(3);
}

function chargersInRpg(data: ReturnType<typeof useDashboard>) {
  const count = data.wsStats?.active_count ?? data.connectedChargers?.count;
  return count != null ? String(count) : "—";
}

function syncStatusClass(status: string | null | undefined) {
  const s = String(status ?? "").toUpperCase();
  if (s === "SYNCED") return "font-semibold text-status-ok";
  if (s === "DIVERGENT") return "font-semibold text-status-error";
  if (s === "DEGRADED") return "font-semibold text-status-warn";
  return "font-medium text-text-tertiary";
}

function SyncTopicLine({
  topic,
  status,
  meterCount,
}: {
  topic: string;
  status: string | null | undefined;
  meterCount?: string | null;
}) {
  const label = status ? String(status).toUpperCase() : "…";
  const showCount = label === "SYNCED" && meterCount;
  return (
    <div className="font-mono text-text-secondary" style={{ fontSize: "var(--font-micro)" }}>
      {topic}:{" "}
      <span className={syncStatusClass(status)}>{label}</span>
      {showCount ? (
        <span className="ml-1 font-normal text-text-tertiary">{meterCount}</span>
      ) : null}
    </div>
  );
}

function SidebarFieldRow({ label, value }: SidebarField) {
  return (
    <div className="flex flex-col gap-0.5">
      <div className="text-text-secondary" style={{ fontSize: "var(--font-micro)" }}>
        {label}
      </div>
      <div className="font-medium text-text-primary" style={{ fontSize: "var(--font-body)" }}>
        {value}
      </div>
    </div>
  );
}

/** Fields shown below the shared “Last sample” block — varies per nav topic. */
function topicSidebarFields(
  topic: TopicId,
  data: ReturnType<typeof useDashboard>,
): SidebarField[] {
  switch (topic) {
    case "ocpp":
      return [
        { label: "MQTT connected", value: data.wsStats?.mqtt_connected ? "Yes" : "No" },
        { label: "Msg in/s", value: data.ocppMsgRate?.in_per_sec?.toFixed(1) ?? "—" },
        { label: "Msg out/s", value: data.ocppMsgRate?.out_per_sec?.toFixed(1) ?? "—" },
      ];
    case "infra": {
      const load = data.hostLoad?.sample?.loadavg;
      const loadStr = load?.length ? load.map((v) => v.toFixed(2)).join(", ") : "—";
      return [
        {
          label: "Open WebSockets",
          value: String(data.gwStatsFull?.active_count ?? data.wsStats?.active_count ?? "—"),
        },
        { label: "Load average", value: loadStr },
        { label: "Infra overall", value: data.infraSnapshot?.snapshot?.overall ?? "—" },
        {
          label: "Core max",
          value:
            data.hostLoad?.sample?.core_max != null
              ? `${data.hostLoad.sample.core_max.toFixed(1)}% (c${data.hostLoad.sample.core_max_idx})`
              : "—",
        },
      ];
    }
    case "frequency":
      return [];
    case "fcr":
      return [
        { label: "Process", value: "fcr-core" },
        { label: "Pieces", value: "modules (in-process)" },
        { label: "Control", value: "fcr/core/cmd" },
      ];
    case "devops":
      return [
        { label: "Access", value: "Admin only" },
        { label: "Focus", value: "MQTT retain · restarts" },
      ];
    case "ecg":
    case "dashboard":
    default:
      return [];
  }
}

function SidebarCoreStatus({
  data,
  meterLiveHz,
  meterLastTs,
  sparklineFrames,
  lastSampleDisplay,
  mainMeterCount,
  powerGamma,
}: {
  data: ReturnType<typeof useDashboard>;
  meterLiveHz: ReturnType<typeof useFreqSparkline>["liveHz"];
  meterLastTs: ReturnType<typeof useFreqSparkline>["lastTs"];
  sparklineFrames: ReturnType<typeof useFreqSparkline>["frames"];
  lastSampleDisplay: string;
  mainMeterCount: string | null;
  powerGamma: number;
}) {
  const cConsensus = mainCConsensus(
    meterLiveHz.main_C,
    meterLiveHz.main_A,
    meterLiveHz.main_B,
    meterLastTs.main_C,
  );
  const cStroke =
    cConsensus === "off"
      ? METER_C_STYLES.off.stroke
      : cConsensus === "stale"
        ? METER_C_STYLES.stale.stroke
        : METER_C_STYLES.agree.stroke;
  const [cShiftView, setCShiftView] = useState(false);

  useEffect(() => {
    if (sparklineFrames.length === 0) setCShiftView(false);
  }, [sparklineFrames.length]);

  return (
    <>
      <div className="space-y-1.5 px-4 py-3">
        <span
          className={[
            "inline-flex rounded-full px-2.5 py-0.5 font-medium",
            data.connection === "live"
              ? "bg-status-ok/15 text-status-ok"
              : "bg-status-error/15 text-status-error",
          ].join(" ")}
          style={{ fontSize: "var(--font-micro)" }}
        >
          {data.connection === "live" ? "Live" : "Disconnected"}
        </span>
        <div className="space-y-0.5 pt-0.5">
          <SyncTopicLine
            topic="freq/main"
            status={data.mainSyncStatus}
            meterCount={mainMeterCount}
          />
          <SyncTopicLine topic="freq/regions" status={data.regionsSyncStatus} />
        </div>
        {data.connectionHint && (
          <p className="text-text-secondary" style={{ fontSize: "var(--font-micro)" }}>
            {data.connectionHint}
          </p>
        )}
      </div>

      <div className="space-y-3 px-4 py-2">
        <div className="flex flex-col gap-0.5">
          <div className="text-text-secondary" style={{ fontSize: "var(--font-micro)" }}>
            Mains frequency
          </div>
          <div
            className="font-medium tabular-nums text-text-primary"
            style={{ fontSize: "var(--font-kpi)" }}
          >
            {formatFreq(data.freqLive?.freq_hz)}
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <div
            className="flex items-baseline justify-between gap-2"
            style={{ fontSize: "var(--font-micro)" }}
          >
            {(["main_A", "main_B"] as const).map((id) => (
              <span
                key={id}
                className="font-medium tabular-nums"
                style={{ color: METER_SPARKLINE_SERIES[id].stroke }}
              >
                {METER_SPARKLINE_SERIES[id].label} {formatFreq(meterLiveHz[id] ?? undefined)}
              </span>
            ))}
          </div>
          <div
            className="flex flex-wrap items-center gap-1.5 text-text-tertiary"
            style={{ fontSize: "10px" }}
          >
            <span className="font-medium tabular-nums" style={{ color: cStroke }}>
              backup: {formatFreq(meterLiveHz.main_C ?? undefined)}
            </span>
            <button
              type="button"
              aria-pressed={cShiftView}
              title="Offset backup trace −5 mHz in chart only"
              onClick={() => setCShiftView((on) => !on)}
              className={`rounded px-1.5 py-px font-medium transition-colors ${
                cShiftView
                  ? "bg-text-tertiary/30 text-text-secondary"
                  : "bg-text-tertiary/15 text-text-tertiary hover:bg-text-tertiary/22"
              }`}
              style={{ fontSize: "9px" }}
            >
              {cShiftView ? "shifted" : "shift view"}
            </button>
          </div>
          <div className="-mx-4">
            <FreqSparkline
              frames={sparklineFrames}
              lastTsC={meterLastTs.main_C}
              shiftView={cShiftView}
            />
          </div>
        </div>

        <SidebarFieldRow label="Last sample" value={lastSampleDisplay} />
        <SidebarFieldRow label="Chargers in RPG:" value={chargersInRpg(data)} />
        <SidebarFieldRow
          label="Current power (MW)"
          value={formatPowerMw(fleetPowerTotalW(data.connectedChargers), powerGamma)}
        />
      </div>
    </>
  );
}

export function Sidebar({ topic }: { topic: TopicId }) {
  const data = useDashboard();
  const auth = useAuth();
  const navigate = useNavigate();
  const { gamma: powerGamma } = usePowerGamma();
  const {
    frames: sparklineFrames,
    lastSampleTsMs: meterLastSampleTsMs,
    lastTs: meterLastTs,
    liveHz: meterLiveHz,
  } = useFreqSparkline(true);
  const { frames: ethFrames, liveTx: ethLiveTx, liveRx: ethLiveRx } = useEthBpsSparkline(true);

  const mainMeterCount = formatMainMeterOnlineCount(meterLastTs);
  const lastSampleDisplay =
    meterLastSampleTsMs != null
      ? formatTsMs(meterLastSampleTsMs)
      : (data.latestSampleMeta ?? "—");

  const topicFields = topicSidebarFields(topic, data);

  return (
    <aside className="flex h-full w-[var(--sidebar-width)] shrink-0 flex-col border-r border-border-subtle bg-card">
      <div className="border-b border-border-subtle px-4 py-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-page text-accent">
            <Server size={20} strokeWidth={1.75} />
          </div>
          <div>
            <div
              className="font-semibold text-text-primary"
              style={{ fontSize: "var(--font-display)" }}
            >
              Blue Current
            </div>
            <div className="text-text-secondary" style={{ fontSize: "var(--font-micro)" }}>
              FCR Dashboard
            </div>
          </div>
        </div>
      </div>

      <SidebarCoreStatus
        data={data}
        meterLiveHz={meterLiveHz}
        meterLastTs={meterLastTs}
        sparklineFrames={sparklineFrames}
        lastSampleDisplay={lastSampleDisplay}
        mainMeterCount={mainMeterCount}
        powerGamma={powerGamma}
      />

      <div className="flex-1 space-y-3 overflow-y-auto px-4 pb-2">
        {topicFields.map((f) => (
          <SidebarFieldRow key={f.label} label={f.label} value={f.value} />
        ))}
      </div>

      <div className="space-y-2 border-t border-border-subtle p-4">
        <div className="flex flex-col gap-1">
          <div
            className="flex items-baseline justify-between gap-2"
            style={{ fontSize: "var(--font-micro)" }}
          >
            <span className="font-medium tabular-nums text-text-primary">
              <span className="text-black">ETH</span>{" "}
              <span style={{ color: ETH_BPS_SPARKLINE_SERIES.tx.stroke }}>
                {ETH_BPS_SPARKLINE_SERIES.tx.label} {formatMbps(ethLiveTx)}
              </span>
            </span>
            <span
              className="font-medium tabular-nums"
              style={{ color: ETH_BPS_SPARKLINE_SERIES.rx.stroke }}
            >
              {ETH_BPS_SPARKLINE_SERIES.rx.label} {formatMbps(ethLiveRx)}
            </span>
          </div>
          <div className="-mx-4 px-0">
            <EthBpsSparkline frames={ethFrames} />
          </div>
        </div>
        <HostDiskReadout hostDisk={data.hostDisk} />
        {auth.user && (
          <div className="text-text-secondary" style={{ fontSize: "var(--font-micro)" }}>
            Signed in as{" "}
            <span className="font-medium text-text-primary">{auth.user.login}</span>
          </div>
        )}
        <button
          type="button"
          className="w-full rounded-md bg-accent px-3 py-2 font-medium text-white transition-opacity hover:opacity-90"
          style={{ fontSize: "var(--font-body)" }}
        >
          System check
        </button>
        <button
          type="button"
          onClick={async () => {
            await auth.logout();
            navigate("/login", { replace: true });
          }}
          className="w-full rounded-md border border-border-subtle px-3 py-2 font-medium text-text-secondary transition-colors hover:bg-page hover:text-text-primary"
          style={{ fontSize: "var(--font-body)" }}
        >
          Sign out
        </button>
      </div>
    </aside>
  );
}
